<template>
  <div class="wco-page py-3">
    <header class="wco-toolbar">
      <div class="wco-toolbar__title">
        <span class="wco-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Website Care-of Verification</h1>
          <p>Credit telesales staff on orders from our ecommerce sites — each is attributed to the agent who actually called the customer's number</p>
        </div>
      </div>
      <div class="wco-toolbar__actions">
        <CButton size="sm" class="wco-apply-all-btn" :disabled="isApplyingAll || !summary.confident" @click="askApplyAll">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{{ isApplyingAll ? 'Crediting…' : `Credit All Confident (${summary.confident || 0})` }}</span>
        </CButton>
        <CButton size="sm" class="wco-recompute-btn" :disabled="isRecomputing" @click="recompute">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ 'is-spinning': isRecomputing }" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isRecomputing ? 'Recomputing…' : 'Recompute' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="wco-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="wco-alert">{{ successMessage }}</CAlert>

    <section class="wco-tiles">
      <article class="wco-tile wco-tile--total">
        <div class="wco-tile__label">Pending Orders</div>
        <div class="wco-tile__value">{{ summary.total }}</div>
        <div class="wco-tile__breakdown"><span>Website orders awaiting care-of</span></div>
      </article>
      <article class="wco-tile wco-tile--confident">
        <div class="wco-tile__label">Confident</div>
        <div class="wco-tile__value">{{ summary.confident }}</div>
        <div class="wco-tile__breakdown"><span>A telesales agent called this customer — one-click credit</span></div>
      </article>
      <article class="wco-tile wco-tile--nomatch">
        <div class="wco-tile__label">No Match</div>
        <div class="wco-tile__value">{{ summary.no_match }}</div>
        <div class="wco-tile__breakdown"><span>No telesales agent has called this number — review to assign</span></div>
      </article>
    </section>

    <section class="wco-section">
      <header class="wco-section__head"><h2>Filters</h2></header>
      <div class="wco-section__body">
        <div class="wco-filters">
          <div class="wco-field wco-field--full">
            <label class="wco-label">Search</label>
            <AppInput no-margin :value="filters.search" @update:value="onSearchInput" placeholder="Search by order number, customer name, or phone…" />
          </div>
          <div class="wco-field">
            <label class="wco-label">From Order Date</label>
            <SingleDatePicker :value="filters.from" @input="filters.from = $event" />
          </div>
          <div class="wco-field">
            <label class="wco-label">To Order Date</label>
            <SingleDatePicker :value="filters.to" @input="filters.to = $event" />
          </div>
          <div class="wco-field">
            <label class="wco-label">Organisation</label>
            <AppDropdown v-model="filters.organisation_id" :options="orgOptions" placeholder="Any Organisation" />
          </div>
          <div class="wco-field">
            <label class="wco-label">Status</label>
            <AppDropdown v-model="filters.status" :options="statusOptions" placeholder="Any Status" />
          </div>
          <div class="wco-field wco-field--action">
            <label class="wco-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="wco-apply-btn" :disabled="isLoading" @click="applyFilters">{{ isLoading ? 'Loading…' : 'Apply' }}</button>
          </div>
          <div class="wco-field wco-field--action">
            <label class="wco-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="wco-reset-btn" @click="resetFilters">Reset</button>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Website Orders"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      max-body-height="65vh"
      empty-text="No pending website orders match these filters."
    >
      <template #toolbar>
        <span v-if="pagination.total" class="wco-count">{{ rangeLabel }}</span>
      </template>
      <template #cell-customer="{ row }">
        <div class="wco-customer-cell">
          <span class="wco-customer-name">{{ row.customer }}</span>
          <span v-if="row.phone && row.phone !== '—'" class="wco-customer-sub">{{ row.phone }}</span>
        </div>
      </template>
      <template #cell-staff="{ row }">
        <span v-if="row._care_of_name" class="wco-staff">{{ row._care_of_name }}</span>
        <span v-else class="wco-muted">—</span>
      </template>
      <template #cell-status="{ row }">
        <span class="wco-pill" :class="statusPillClass(row._status)">{{ statusLabel(row._status) }}</span>
        <div v-if="row._reason" class="wco-reason">{{ row._reason }}</div>
      </template>
      <template #cell-action="{ row }">
        <div class="wco-row-actions">
          <template v-if="row._status === 'confident'">
            <button type="button" class="wco-act-btn" :disabled="applyingIds[row._id]" @click="applyConfident(row)">
              {{ applyingIds[row._id] ? '…' : 'Credit' }}
            </button>
            <button type="button" class="wco-act-btn wco-act-btn--ghost" @click="openReview(row)">Review</button>
          </template>
          <button
            v-else-if="row._status === 'needs_review' || row._status === 'no_match'"
            type="button"
            class="wco-act-btn wco-act-btn--review"
            @click="openReview(row)"
          >
            Review
          </button>
          <button
            v-else-if="row._status === 'credited'"
            type="button"
            class="wco-act-btn wco-act-btn--danger"
            @click="askUncredit(row)"
          >
            Uncredit
          </button>
          <button
            v-else-if="row._status === 'rejected' || row._status === 'oms_customer'"
            type="button"
            class="wco-act-btn wco-act-btn--ghost"
            @click="askReopen(row)"
          >
            Reopen
          </button>
          <span v-else class="wco-muted">—</span>
        </div>
      </template>
    </BaseTable>

    <div v-if="!isLoading && tableRows.length && pagination.last_page > 1" class="wco-pagination-bar">
      <span class="wco-pagination-info">{{ rangeLabel }}</span>
      <AppPager :current-page="Number(pagination.current_page) || 1" :last-page="Number(pagination.last_page) || 1" @change="goToPage" />
    </div>

    <CModal
      :show.sync="reviewOpen"
      size="lg"
      :centered="true"
      title="Credit Website Order"
      class="wco-review-modal"
      :close-on-backdrop="!isApprovingReview"
    >
      <div v-if="reviewRow" class="wco-review-body">
        <div v-if="reviewError" class="wco-review-alert">{{ reviewError }}</div>

        <div class="wco-review-order">
          <div class="wco-review-order__head">
            <span class="wco-review-eyebrow">Website Order</span>
            <h3>{{ reviewRow.customer || '—' }}</h3>
          </div>
          <div class="wco-review-order__grid">
            <div><span class="wco-rk">Order #</span><span class="wco-rv">{{ reviewRow.order_number || '—' }}</span></div>
            <div><span class="wco-rk">Phone</span><span class="wco-rv">{{ reviewRow.phone || '—' }}</span></div>
            <div><span class="wco-rk">Order Date</span><span class="wco-rv">{{ reviewRow.order_date || '—' }}</span></div>
          </div>
        </div>

        <div v-if="suggestedAgent" class="wco-suggested">
          <span class="wco-rk">Suggested (telesales agent who called this number)</span>
          <span class="wco-staff">{{ suggestedAgent.name }}</span>
        </div>

        <div class="wco-review-head">
          <h4>Credit Agent <span class="wco-req">*</span></h4>
          <p>Pick the telesales agent to credit for this order. Defaults to the agent who called the customer's number, when one is found; only active (non-departed) telesales staff in the order's organisation are listed.</p>
        </div>
        <div v-if="isLoadingCandidates" class="wco-review-empty">Loading agents…</div>
        <div v-else-if="!agentOptions.length" class="wco-review-empty">No active telesales staff in this organisation to credit.</div>
        <div v-else class="wco-manual-assign">
          <AppDropdown v-model="selectedAgentId" :options="agentOptions" searchable search-placeholder="Search agent…" placeholder="Select an agent" />
        </div>
      </div>
      <template #footer>
        <div class="wco-modal-foot">
          <div class="wco-modal-foot__left">
            <button type="button" class="wco-modal-btn wco-modal-btn--danger" :disabled="isApprovingReview" @click="dismissFromReview('rejected')">Reject</button>
            <button type="button" class="wco-modal-btn wco-modal-btn--warn" :disabled="isApprovingReview" @click="dismissFromReview('oms_customer')">OMS Customer</button>
          </div>
          <div class="wco-modal-foot__right">
            <button type="button" class="wco-modal-btn wco-modal-btn--ghost" :disabled="isApprovingReview" @click="closeReview">Cancel</button>
            <button type="button" class="wco-modal-btn wco-modal-btn--primary" :disabled="isApprovingReview || !selectedAgentId" @click="approveReview">
              {{ isApprovingReview ? 'Crediting…' : 'Credit Care-of' }}
            </button>
          </div>
        </div>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="confirmApplyAllOpen"
      title="Credit All Confident Orders?"
      :message="applyAllMessage"
      confirm-label="Credit All"
      busy-label="Crediting…"
      cancel-label="Cancel"
      :busy="isApplyingAll"
      variant="warning"
      @confirm="confirmApplyAll"
      @cancel="cancelApplyAll"
      @update:visible="(v) => !v && cancelApplyAll()"
    />

    <AppConfirmDialog
      :visible="rowActionOpen"
      :title="rowAction ? rowAction.title : ''"
      :message="rowAction ? rowAction.message : ''"
      :confirm-label="rowAction ? rowAction.confirmLabel : 'Confirm'"
      busy-label="Working…"
      cancel-label="Cancel"
      :busy="rowActionBusy"
      :variant="rowAction ? rowAction.variant : 'warning'"
      @confirm="confirmRowAction"
      @cancel="cancelRowAction"
      @update:visible="(v) => !v && cancelRowAction()"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppInput from '@/components/AppInput.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import { ORGANISATION_ID_TO_NAME } from '@/views/users/userHelpers.js'

function todayIso () {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const DEFAULT_SUMMARY = { total: 0, confident: 0, needs_review: 0, no_match: 0, credited: 0, rejected: 0, oms_customer: 0 }

export default {
  name: 'WebsiteCareOf',
  components: { AppInput, AppDropdown, SingleDatePicker, BaseTable, AppPager, AppConfirmDialog },
  data () {
    return {
      filters: { from: '', to: todayIso(), organisation_id: '', status: '', search: '' },
      searchDebounce: null,
      rows: [],
      summary: { ...DEFAULT_SUMMARY },
      pagination: { current_page: 1, per_page: 25, total: 0, last_page: 1 },
      isLoading: false,
      isRecomputing: false,
      isApplyingAll: false,
      applyingIds: {},
      confirmApplyAllOpen: false,
      errorMessage: '',
      successMessage: '',
      reviewOpen: false,
      reviewRow: null,
      reviewError: '',
      isLoadingCandidates: false,
      isApprovingReview: false,
      suggestedAgent: null,
      reviewAgents: [],
      selectedAgentId: '',
      rowActionOpen: false,
      rowAction: null,
      rowActionBusy: false
    }
  },
  computed: {
    columns () {
      return [
        { key: 'sno',          label: 'SNo',          width: '64px' },
        { key: 'order_number', label: 'Order #',      width: '140px', sticky: true },
        { key: 'customer',     label: 'Customer',     width: '240px', sticky: true },
        { key: 'order_date',   label: 'Order Date',   width: '160px' },
        { key: 'staff',        label: 'Suggested Care-of', width: '180px' },
        { key: 'status',       label: 'Status' },
        { key: 'action',       label: 'Action',       width: '160px', align: 'center', stickyRight: true }
      ]
    },
    tableRows () {
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      return this.rows.map((row, index) => {
        const o = row.order || {}
        return {
          sno: (page - 1) * perPage + index + 1,
          order_number: o.order_number || ('#' + row.order_id),
          customer: o.contact_name || '—',
          phone: o.contact_phone || '—',
          order_date: this.orderDateDisplay(o),
          _id: row.order_id,
          _status: row.status,
          _reason: row.reason,
          _care_of_id: row.care_of_user_id,
          _care_of_name: row.care_of_user_name
        }
      })
    },
    orgOptions () {
      return [{ value: '', label: 'Any Organisation' }].concat(
        Object.keys(ORGANISATION_ID_TO_NAME).map((id) => ({ value: String(id), label: ORGANISATION_ID_TO_NAME[id] }))
      )
    },
    statusOptions () {
      const s = this.summary || {}
      const tag = (n) => (Number(n) > 0 ? ` (${n})` : '')
      return [
        { value: '',             label: 'Any Status' },
        { value: 'confident',    label: 'Confident' },
        { value: 'no_match',     label: 'No Match' },
        { value: 'credited',     label: `Credited${tag(s.credited)}` },
        { value: 'rejected',     label: `Rejected${tag(s.rejected)}` },
        { value: 'oms_customer', label: `OMS Customer${tag(s.oms_customer)}` }
      ]
    },
    agentOptions () {
      return this.reviewAgents.map((u) => ({ value: String(u.id), label: u.name }))
    },
    applyAllMessage () {
      const count = Number(this.summary.confident) || 0
      return `${count} confident order${count === 1 ? '' : 's'} will be credited to each organisation's last active telecaller. Each order is re-checked before writing.`
    },
    rangeLabel () {
      const total = Number(this.pagination.total) || 0
      if (!total) return '0 orders'
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      const start = (page - 1) * perPage + 1
      const end = Math.min(page * perPage, total)
      return `${start}–${end} of ${total}`
    }
  },
  created () {
    this.refresh()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    parseError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data) {
        if (typeof data === 'string') return data
        if (data.message) return data.message
        if (data.errors) {
          const k = Object.keys(data.errors)[0]
          if (k && Array.isArray(data.errors[k])) return data.errors[k][0]
        }
      }
      return fallback
    },
    // Order.created_at is Unix seconds; prefer order_date (ISO datetime).
    orderDateDisplay (o) {
      if (!o) return '—'
      if (o.order_date) return this.formatDateTime(o.order_date)
      const ts = Number(o.created_at)
      if (Number.isFinite(ts) && ts > 0) return this.formatDateTime(ts * 1000)
      return '—'
    },
    formatDateTime (value) {
      const d = new Date(value)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    queryParams () {
      const p = { token: this.authToken() }
      if (this.filters.from) p.from = this.filters.from
      if (this.filters.to) p.to = this.filters.to
      if (this.filters.organisation_id) p.organisation_id = this.filters.organisation_id
      if (this.filters.status) p.status = this.filters.status
      if (this.filters.search && this.filters.search.trim()) p.search = this.filters.search.trim()
      return p
    },
    refresh () {
      this.errorMessage = ''
      this.successMessage = ''
      this.loadSummary()
      this.loadPreview(1)
    },
    loadSummary () {
      axios.get(`${this.$apiAdress}/api/website-care-of/summary`, { params: this.queryParams() })
        .then((response) => {
          this.summary = { ...DEFAULT_SUMMARY, ...(response && response.data ? response.data : {}) }
        })
        .catch((error) => {
          console.log(error)
          this.summary = { ...DEFAULT_SUMMARY }
        })
    },
    loadPreview (page) {
      this.isLoading = true
      const targetPage = Number(page) || Number(this.pagination.current_page) || 1
      const params = Object.assign({}, this.queryParams(), { page: targetPage, per_page: this.pagination.per_page || 25 })

      axios.get(`${this.$apiAdress}/api/website-care-of/preview`, { params })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.rows = Array.isArray(data.data) ? data.data : []
          this.pagination = {
            current_page: Number(data.current_page) || 1,
            per_page: Number(data.per_page) || 25,
            total: Number(data.total) || 0,
            last_page: Number(data.last_page) || 1
          }
        })
        .catch((error) => {
          console.log(error)
          this.rows = []
          this.errorMessage = this.parseError(error, 'Unable to load the website order preview.')
        })
        .finally(() => { this.isLoading = false })
    },
    applyFilters () {
      this.refresh()
    },
    onSearchInput (value) {
      this.filters.search = value
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => { this.refresh() }, 400)
    },
    resetFilters () {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.filters = { from: '', to: todayIso(), organisation_id: '', status: '', search: '' }
      this.refresh()
    },
    goToPage (page) {
      if (Number(page) === Number(this.pagination.current_page)) return
      this.loadPreview(Number(page))
    },
    applyConfident (row) {
      const id = row._id
      if (!id || this.applyingIds[id]) return
      this.$set(this.applyingIds, id, true)
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/website-care-of/${id}/apply?token=${this.authToken()}`, {
        care_of_user_id: row._care_of_id
      })
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Credited.'
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Credit failed.')
        })
        .finally(() => { this.$set(this.applyingIds, id, false) })
    },
    openReview (row) {
      this.reviewRow = row
      this.reviewError = ''
      this.suggestedAgent = null
      this.reviewAgents = []
      this.selectedAgentId = ''
      this.reviewOpen = true
      this.loadCandidates(row._id)
    },
    closeReview () {
      if (this.isApprovingReview) return
      this.reviewOpen = false
      this.reviewRow = null
      this.reviewError = ''
      this.suggestedAgent = null
      this.reviewAgents = []
      this.selectedAgentId = ''
    },
    loadCandidates (id) {
      if (!id) return
      this.isLoadingCandidates = true
      axios.get(`${this.$apiAdress}/api/website-care-of/${id}/candidates`, { params: { token: this.authToken() } })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.suggestedAgent = data.suggested || null
          this.reviewAgents = Array.isArray(data.agents) ? data.agents : []
          // Pre-select the suggested last-active telecaller.
          if (this.suggestedAgent && this.suggestedAgent.user_id) {
            this.selectedAgentId = String(this.suggestedAgent.user_id)
          }
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Unable to load agents.')
        })
        .finally(() => { this.isLoadingCandidates = false })
    },
    approveReview () {
      if (!this.reviewRow || !this.selectedAgentId) return
      this.isApprovingReview = true
      this.reviewError = ''

      axios.post(`${this.$apiAdress}/api/website-care-of/${this.reviewRow._id}/apply?token=${this.authToken()}`, {
        care_of_user_id: Number(this.selectedAgentId)
      })
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Credited.'
          this.reviewOpen = false
          this.reviewRow = null
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Credit failed.')
        })
        .finally(() => { this.isApprovingReview = false })
    },
    // Dismiss a pending order from inside the review modal without crediting.
    dismissFromReview (status) {
      if (!this.reviewRow || this.isApprovingReview) return
      this.isApprovingReview = true
      this.reviewError = ''

      axios.post(`${this.$apiAdress}/api/website-care-of/${this.reviewRow._id}/status?token=${this.authToken()}`, { status })
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Updated.'
          this.reviewOpen = false
          this.reviewRow = null
          this.selectedAgentId = ''
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Update failed.')
        })
        .finally(() => { this.isApprovingReview = false })
    },
    askUncredit (row) {
      this.rowAction = {
        kind: 'uncredit',
        id: row._id,
        title: 'Reverse this credit?',
        message: 'The care-of credit will be removed and the order returned to the pending list so it can be re-credited or dismissed.',
        confirmLabel: 'Uncredit',
        variant: 'danger'
      }
      this.rowActionOpen = true
    },
    askReopen (row) {
      const what = row._status === 'rejected' ? 'rejected' : 'OMS-customer'
      this.rowAction = {
        kind: 'reopen',
        id: row._id,
        title: 'Re-open to pending?',
        message: `This ${what} order will return to the pending list to be credited or dismissed again.`,
        confirmLabel: 'Re-open',
        variant: 'warning'
      }
      this.rowActionOpen = true
    },
    confirmRowAction () {
      const a = this.rowAction
      if (!a || this.rowActionBusy) return
      this.rowActionBusy = true
      this.errorMessage = ''
      this.successMessage = ''

      const url = a.kind === 'uncredit'
        ? `${this.$apiAdress}/api/website-care-of/${a.id}/uncredit?token=${this.authToken()}`
        : `${this.$apiAdress}/api/website-care-of/${a.id}/status?token=${this.authToken()}`
      const body = a.kind === 'uncredit' ? {} : { status: 'pending' }

      axios.post(url, body)
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Done.'
          this.rowActionOpen = false
          this.rowAction = null
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Action failed.')
          this.rowActionOpen = false
        })
        .finally(() => { this.rowActionBusy = false })
    },
    cancelRowAction () {
      if (this.rowActionBusy) return
      this.rowActionOpen = false
      this.rowAction = null
    },
    askApplyAll () {
      if (this.isApplyingAll || !this.summary.confident) return
      this.confirmApplyAllOpen = true
    },
    confirmApplyAll () {
      this.isApplyingAll = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/website-care-of/apply-all?token=${this.authToken()}`, this.queryParams())
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Credited.'
          this.confirmApplyAllOpen = false
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Bulk credit failed.')
          this.confirmApplyAllOpen = false
        })
        .finally(() => { this.isApplyingAll = false })
    },
    cancelApplyAll () {
      if (this.isApplyingAll) return
      this.confirmApplyAllOpen = false
    },
    recompute () {
      this.isRecomputing = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/website-care-of/recompute?token=${this.authToken()}`, this.queryParams())
        .then((response) => {
          this.summary = { ...DEFAULT_SUMMARY, ...(response && response.data ? response.data : {}) }
          this.successMessage = 'Recompute complete.'
          this.loadPreview(1)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Recompute failed.')
        })
        .finally(() => { this.isRecomputing = false })
    },
    statusLabel (status) {
      switch (status) {
        case 'confident':    return 'Confident'
        case 'needs_review': return 'Needs Review'
        case 'no_match':     return 'No Match'
        case 'credited':     return 'Credited'
        case 'rejected':     return 'Rejected'
        case 'oms_customer': return 'OMS Customer'
        default:             return status || '—'
      }
    },
    statusPillClass (status) {
      switch (status) {
        case 'confident':    return 'wco-pill--green'
        case 'needs_review': return 'wco-pill--amber'
        case 'no_match':     return 'wco-pill--gray'
        case 'credited':     return 'wco-pill--blue'
        case 'rejected':     return 'wco-pill--red'
        case 'oms_customer': return 'wco-pill--violet'
        default:             return 'wco-pill--gray'
      }
    }
  }
}
</script>

<style scoped>
.wco-page { padding: 0.25rem 0; }

.wco-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; }
.wco-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.wco-toolbar__title h1 { margin: 0; font-size: 1.15rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.wco-toolbar__title p  { margin: 0.1rem 0 0; font-size: 0.78rem; color: #6b7280; }
.wco-toolbar__accent { width: 4px; height: 32px; border-radius: 4px; background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%); }
.wco-toolbar__actions { display: inline-flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }

.wco-recompute-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 34px; padding: 0 0.9rem; border-radius: 8px;
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
  font-weight: 600; font-size: 0.82rem; cursor: pointer;
}
.wco-recompute-btn:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.wco-recompute-btn:disabled { opacity: 0.7; cursor: progress; }
.wco-recompute-btn svg.is-spinning { animation: wco-spin 0.9s linear infinite; }
.wco-recompute-btn:focus { outline: none; }
.wco-recompute-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.wco-apply-all-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 34px; padding: 0 0.9rem; border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #fff; font-weight: 600; font-size: 0.82rem;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); cursor: pointer;
}
.wco-apply-all-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.wco-apply-all-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.wco-apply-all-btn:focus { outline: none; }
.wco-apply-all-btn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
@keyframes wco-spin { to { transform: rotate(360deg); } }

.wco-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.wco-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.85rem; margin-bottom: 1rem; }
.wco-tile { background: #ffffff; border: 1px solid #e5e9f2; border-radius: 12px; padding: 0.9rem 1rem; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; }
.wco-tile__label { font-size: 0.74rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.wco-tile__value { font-size: 1.5rem; font-weight: 700; color: #1f2a44; }
.wco-tile__breakdown { display: flex; flex-direction: column; gap: 0.1rem; font-size: 0.72rem; color: #64748b; }
.wco-tile--confident .wco-tile__value { color: #166534; }
.wco-tile--review .wco-tile__value { color: #92400e; }
.wco-tile--nomatch .wco-tile__value { color: #6b7280; }

.wco-section { background: #ffffff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); margin-bottom: 1rem; }
.wco-section__head { padding: 0.7rem 1rem; border-bottom: 1px solid #eef1f7; background: #fbfcfe; border-radius: 12px 12px 0 0; }
.wco-section__head h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44; }
.wco-section__body { padding: 1rem; }

.wco-label { display: block; margin-bottom: 0.3rem; font-size: 0.78rem; font-weight: 600; color: #4f5560; }
.wco-filters { display: flex; flex-wrap: wrap; gap: 0.85rem; align-items: flex-end; }
.wco-field { display: flex; flex-direction: column; flex: 1 1 200px; min-width: 0; }
.wco-field--action { flex: 0 0 auto; min-width: 110px; }
.wco-field--full { flex: 1 1 100%; }

.wco-apply-btn {
  height: 36px; padding: 0 1.1rem; border-radius: 8px; border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-weight: 600; font-size: 0.85rem; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); cursor: pointer;
}
.wco-apply-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.wco-apply-btn:disabled { opacity: 0.55; cursor: progress; }
.wco-apply-btn:focus { outline: none; }
.wco-apply-btn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }

.wco-reset-btn { height: 36px; padding: 0 1rem; border-radius: 8px; background: #ffffff; border: 1px solid #d8dde6; color: #1f2a44; font-weight: 600; font-size: 0.85rem; cursor: pointer; }
.wco-reset-btn:hover { border-color: #94a3b8; }
.wco-reset-btn:focus { outline: none; }
.wco-reset-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.wco-count { font-size: 0.78rem; color: #6b7280; }

.wco-customer-cell { display: flex; flex-direction: column; gap: 0.1rem; }
.wco-customer-name { color: #1f2a44; font-weight: 500; }
.wco-customer-sub { font-size: 0.72rem; color: #64748b; }
.wco-staff { color: #1f2a44; font-weight: 500; }
.wco-muted { color: #94a3b8; }

.wco-reason { font-size: 0.72rem; color: #64748b; margin-top: 0.2rem; max-width: 360px; }

.wco-pill { display: inline-block; padding: 0.18rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; }
.wco-pill--green  { background: #dcfce7; color: #166534; }
.wco-pill--amber  { background: #fef3c7; color: #92400e; }
.wco-pill--gray   { background: #e5e7eb; color: #374151; }
.wco-pill--blue   { background: #dbeafe; color: #1e40af; }
.wco-pill--red    { background: #fee2e2; color: #991b1b; }
.wco-pill--violet { background: #ede9fe; color: #5b21b6; }

.wco-row-actions { display: inline-flex; gap: 0.35rem; flex-wrap: wrap; align-items: center; justify-content: center; }

.wco-act-btn {
  height: 28px; padding: 0 0.7rem; border: 0; border-radius: 6px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-size: 0.75rem; font-weight: 700; cursor: pointer; letter-spacing: 0.02em;
  box-shadow: 0 1px 3px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.wco-act-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 4px 10px rgba(14, 91, 70, 0.28); }
.wco-act-btn:disabled { opacity: 0.55; cursor: progress; }
.wco-act-btn:focus { outline: none; }
.wco-act-btn:focus-visible { outline: none; box-shadow: 0 1px 3px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
.wco-act-btn--review { background: linear-gradient(135deg, #b45309 0%, #d97706 100%); box-shadow: 0 1px 3px rgba(180, 83, 9, 0.22); }
.wco-act-btn--review:hover:not(:disabled) { box-shadow: 0 4px 10px rgba(180, 83, 9, 0.32); }
.wco-act-btn--danger { background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%); box-shadow: 0 1px 3px rgba(185, 28, 28, 0.22); }
.wco-act-btn--danger:hover:not(:disabled) { box-shadow: 0 4px 10px rgba(185, 28, 28, 0.32); }
.wco-act-btn--danger:focus-visible { outline: none; box-shadow: 0 1px 3px rgba(185, 28, 28, 0.22), 0 0 0 3px rgba(220, 38, 38, 0.30); }
.wco-act-btn--ghost { background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44; box-shadow: none; }
.wco-act-btn--ghost:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; box-shadow: none; transform: none; }
.wco-act-btn--ghost:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.wco-pagination-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.85rem; }
.wco-pagination-info { font-size: 0.78rem; color: #6b7280; }

/* Review modal */
.wco-review-body { display: flex; flex-direction: column; gap: 0.9rem; }
.wco-review-alert { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; border-radius: 8px; padding: 0.5rem 0.7rem; font-size: 0.82rem; }
.wco-review-order { background: #fbfcfe; border: 1px solid #eef1f7; border-radius: 10px; padding: 0.85rem; }
.wco-review-order__head { display: flex; flex-direction: column; gap: 0.1rem; margin-bottom: 0.5rem; }
.wco-review-eyebrow { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #16a07a; }
.wco-review-order__head h3 { margin: 0; font-size: 1rem; font-weight: 700; color: #1f2a44; }
.wco-review-order__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.4rem 1rem; }
.wco-rk { display: block; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: #94a3b8; }
.wco-rv { font-size: 0.85rem; color: #1f2a44; }

.wco-suggested { display: flex; flex-direction: column; gap: 0.15rem; padding: 0.6rem 0.85rem; background: #f0fbf6; border: 1px solid #bbf7d0; border-radius: 10px; }

.wco-review-head { margin-top: 0.2rem; }
.wco-review-head h4 { margin: 0; font-size: 0.9rem; font-weight: 700; color: #1f2a44; }
.wco-review-head p { margin: 0.2rem 0 0; font-size: 0.76rem; color: #6b7280; }
.wco-req { color: #dc2626; }
.wco-review-empty { font-size: 0.82rem; color: #64748b; padding: 0.5rem 0; }
.wco-manual-assign { display: flex; flex-direction: column; gap: 0.5rem; }

.wco-modal-btn { height: 34px; min-width: 90px; padding: 0.45rem 1.1rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; }
.wco-modal-btn--ghost { background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44; }
.wco-modal-btn--ghost:hover { background: #f8fafc; }
.wco-modal-btn--primary { border: 0; color: #fff; background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18); }
.wco-modal-btn--primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.wco-modal-btn--danger { border: 0; color: #fff; background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%); box-shadow: 0 2px 6px rgba(185, 28, 28, 0.18); }
.wco-modal-btn--danger:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(185, 28, 28, 0.28); filter: brightness(1.05); }
.wco-modal-btn--warn { border: 0; color: #fff; background: linear-gradient(135deg, #b45309 0%, #d97706 100%); box-shadow: 0 2px 6px rgba(180, 83, 9, 0.18); }
.wco-modal-btn--warn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(180, 83, 9, 0.28); filter: brightness(1.05); }
.wco-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.wco-modal-btn:focus { outline: none; }
.wco-modal-btn--ghost:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.wco-modal-btn--primary:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.30); }
.wco-modal-btn--danger:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(185, 28, 28, 0.18), 0 0 0 3px rgba(220, 38, 38, 0.30); }
.wco-modal-btn--warn:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(180, 83, 9, 0.18), 0 0 0 3px rgba(217, 119, 6, 0.30); }

.wco-modal-foot { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; width: 100%; flex-wrap: wrap; }
.wco-modal-foot__left, .wco-modal-foot__right { display: inline-flex; gap: 0.5rem; align-items: center; }
</style>
